/**
 * PA Animation Components
 * Typing indicator, voice waveform, count-up, confetti, haptic
 */
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

// ==================== Typing Indicator ====================
export function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 py-1">
      <span className="w-2 h-2 rounded-full bg-[var(--v4-accent)] animate-[bounce_1.4s_infinite_0s]" />
      <span className="w-2 h-2 rounded-full bg-[var(--v4-accent)] animate-[bounce_1.4s_infinite_0.2s]" />
      <span className="w-2 h-2 rounded-full bg-[var(--v4-accent)] animate-[bounce_1.4s_infinite_0.4s]" />
    </div>
  );
}

// ==================== Voice Waveform ====================
export function VoiceWaveform({ isActive }: { isActive: boolean }) {
  return (
    <div className="flex items-center justify-center gap-[3px] h-12">
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'w-[3px] rounded-full transition-all duration-150',
            isActive ? 'bg-red-400' : 'bg-[#7c75f0]/40'
          )}
          style={{
            height: isActive ? `${12 + Math.random() * 36}px` : '8px',
            animation: isActive ? `waveform 0.8s ease-in-out ${i * 0.07}s infinite alternate` : 'none',
          }}
        />
      ))}
      <style>{`
        @keyframes waveform {
          0% { height: 8px; }
          100% { height: ${12 + Math.random() * 36}px; }
        }
      `}</style>
    </div>
  );
}

// ==================== Count Up Animation ====================
export function CountUp({ end, duration = 1000, prefix = '', suffix = '' }: { end: number; duration?: number; prefix?: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<number>(0);

  useEffect(() => {
    if (end === 0) { setCount(0); return; }
    const startTime = Date.now();
    const startVal = ref.current;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = Math.round(startVal + (end - startVal) * eased);
      setCount(current);
      if (progress < 1) requestAnimationFrame(animate);
      else ref.current = end;
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return <>{prefix}{count.toLocaleString()}{suffix}</>;
}

// ==================== Success Checkmark ====================
export function SuccessCheck({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-none">
      <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center animate-[scaleIn_0.3s_ease-out]">
        <svg className="w-10 h-10 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 13l4 4L19 7" className="animate-[drawCheck_0.4s_ease-out_0.2s_both]" style={{ strokeDasharray: 24, strokeDashoffset: 24 }} />
        </svg>
      </div>
      <style>{`
        @keyframes scaleIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes drawCheck { to { stroke-dashoffset: 0; } }
      `}</style>
    </div>
  );
}

// ==================== Haptic Feedback ====================
export function haptic(type: 'light' | 'medium' | 'heavy' = 'light') {
  if (!navigator.vibrate) return;
  const patterns: Record<string, number[]> = { light: [10], medium: [20], heavy: [30, 10, 30] };
  navigator.vibrate(patterns[type] || [10]);
}

// ==================== Pull to Refresh ====================
export function usePullToRefresh(onRefresh: () => Promise<void>, threshold = 80) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (scrollRef.current && scrollRef.current.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setPulling(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!pulling) return;
    const diff = e.touches[0].clientY - startY.current;
    if (diff > 0) setPullDistance(Math.min(diff * 0.5, threshold * 1.5));
  };

  const handleTouchEnd = async () => {
    if (pullDistance >= threshold && !refreshing) {
      setRefreshing(true);
      haptic('medium');
      await onRefresh();
      setRefreshing(false);
    }
    setPulling(false);
    setPullDistance(0);
  };

  return { scrollRef, pullDistance, refreshing, handleTouchStart, handleTouchMove, handleTouchEnd };
}

// ==================== Swipe Action ====================
export function useSwipe(onSwipeLeft?: () => void, onSwipeRight?: () => void, threshold = 80) {
  const startX = useRef(0);
  const [offsetX, setOffsetX] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => { startX.current = e.touches[0].clientX; };
  const handleTouchMove = (e: React.TouchEvent) => {
    const diff = e.touches[0].clientX - startX.current;
    setOffsetX(diff);
  };
  const handleTouchEnd = () => {
    if (offsetX > threshold && onSwipeRight) { onSwipeRight(); haptic('light'); }
    else if (offsetX < -threshold && onSwipeLeft) { onSwipeLeft(); haptic('light'); }
    setOffsetX(0);
  };

  return { offsetX, handleTouchStart, handleTouchMove, handleTouchEnd };
}
